<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Class Subjects Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Class Subject
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by class, subject, teacher, or schedule..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.class_id"
                        :options="classOptions"
                        placeholder="All Classes"
                        :option-label="getClassLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchClassSubjects"
                    />

                    <!-- Subject Filter -->
                    <SearchableSelect
                        v-model="filters.subject_id"
                        :options="subjectOptions"
                        placeholder="All Subjects"
                        :option-label="getSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchClassSubjects"
                    />

                    <!-- Teacher Filter -->
                    <SearchableSelect
                        v-model="filters.teacher_id"
                        :options="teacherOptions"
                        placeholder="All Teachers"
                        :option-label="getTeacherName"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchClassSubjects"
                    />

                    <!-- Semester Filter -->
                    <SearchableSelect
                        v-model="filters.semester_id"
                        :options="semesterOptions"
                        placeholder="All Semesters"
                        :option-label="getSemesterLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchClassSubjects"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchClassSubjects"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="classSubjectStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="8" />
        </div>

        <!-- Error State -->
        <div
            v-if="classSubjectStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ classSubjectStore.error }}
                    </p>
                    <button
                        @click="classSubjectStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!classSubjectStore.loading && !classSubjectStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Subject
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Teacher
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Semester
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Schedule
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Room
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="classSubject in classSubjectStore.classSubjects"
                            :key="classSubject.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ classSubject.class?.grade_level || "-" }}
                                    -
                                    {{ classSubject.class?.name || "Unknown" }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ classSubject.subject?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ classSubject.teacher?.user?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ classSubject.semester?.name || "-" }}
                                /
                                {{
                                    classSubject.semester?.academic_year
                                        ?.name ||
                                    classSubject.semester?.academic_year
                                        ?.year ||
                                    "No Academic Year"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ classSubject.schedule || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ classSubject.room || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="classSubject.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editClassSubject(classSubject)"
                                    title="Edit Class Subject"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(classSubject)"
                                    title="Delete Class Subject"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="classSubjectStore.classSubjects.length === 0"
                :icon="BookOpen"
                title="No class subjects found"
                description="Get started by adding a new class subject."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Class Subject
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="classSubjectStore.meta"
                :per-page="classSubjectStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Class Subject Modal -->
        <ClassSubjectModal
            :showModal="showModal"
            :class-subject="selectedClassSubject"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useClassSubjectStore } from "@/stores/classSubjectStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import ClassSubjectModal from "@/components/ClassSubjectModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import classSchoollApi, { type ClassSchooll } from "@/api/classSchooll";
import subjectApi, { type Subject } from "@/api/subject";
import teacherApi, { type Teacher } from "@/api/teacher";
import semesterApi, { type Semester } from "@/api/semester";
import type { ClassSubject } from "@/api/classSubject";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen } from "@lucide/vue";

const classSubjectStore = useClassSubjectStore();
// State
const searchQuery = ref("");
const filters = ref({
    class_id: "",
    subject_id: "",
    teacher_id: "",
    semester_id: "",
    is_active: null as boolean | null,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedClassSubject = ref<ClassSubject | null>(null);
const isEditing = ref(false);
const classSubjectToDelete = ref<ClassSubject | null>(null);
// Types for options with "Select All" option
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SubjectOption = Subject | { id: ""; name: string; code: string };
type TeacherOption = Teacher | { id: ""; user: { name: string } };
type SemesterOption =
    Semester | { id: ""; name: string; academic_year: { name: string } };

const classOptions = ref<ClassOption[]>([]);
const subjectOptions = ref<SubjectOption[]>([]);
const teacherOptions = ref<TeacherOption[]>([]);
const semesterOptions = ref<SemesterOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this class subject? This action cannot be undone.",
);

// Computed
const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.class_id !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.teacher_id !== "" ||
        filters.value.semester_id !== "" ||
        filters.value.is_active !== null ||
        classSubjectStore.page !== 1 ||
        classSubjectStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    classSubjectStore.perPage = value;
    classSubjectStore.page = 1;
    fetchClassSubjects();
};

const fetchClassSubjects = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await classSubjectStore.fetchClassSubjects(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [
            classesResponse,
            subjectsResponse,
            teachersResponse,
            semestersResponse,
        ] = await Promise.all([
            classSchoollApi.getAll({ per_page: 1000 }),
            subjectApi.getAll({ per_page: 1000 }),
            teacherApi.getAll({ per_page: 1000 }),
            semesterApi.getSemesters({ per_page: 1000 }),
        ]);
        // Add "Select All" option as the first element
        const classData: any = classesResponse.data;
        const subjectData: any = subjectsResponse.data;
        const teacherData: any = teachersResponse.data;
        const semesterData: any = semestersResponse.data;

        classOptions.value = [
            { id: "", grade_level: "", name: "All Classes" },
            ...(classData?.items || []),
        ];
        subjectOptions.value = [
            { id: "", name: "All Subjects", code: "" },
            ...(subjectData?.items || []),
        ];
        teacherOptions.value = [
            { id: "", user: { name: "All Teachers" } },
            ...(teacherData?.items || []),
        ];
        semesterOptions.value = [
            { id: "", name: "All Semesters", academic_year: { name: "" } },
            ...(semesterData?.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSubjectLabel = (subjectOption: SubjectOption): string => {
    if (subjectOption.id === "") return subjectOption.name;
    return `${subjectOption.name} (${subjectOption.code})`;
};

const getTeacherName = (teacher: TeacherOption): string => {
    return teacher.user?.name || "All Teachers";
};

const getSemesterLabel = (semester: SemesterOption): string => {
    if (semester.id === "") return semester.name;
    return `${semester.name} - ${semester.academic_year?.name}`;
};

const debouncedSearch = useDebounce(fetchClassSubjects, 300);

const openModal = () => {
    selectedClassSubject.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editClassSubject = (classSubject: ClassSubject) => {
    selectedClassSubject.value = classSubject;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedClassSubject.value = null;
    isEditing.value = false;
};

const confirmDelete = (classSubject: ClassSubject) => {
    classSubjectToDelete.value = classSubject;
    deleteModalTitle.value = "Delete Class Subject";
    deleteModalMessage.value = `Are you sure you want to delete "${classSubject.display_name || classSubject.id}"? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    classSubjectToDelete.value = null;
};

const handleSaved = () => {
    // Refresh class subjects list to show new/updated class subject
    fetchClassSubjects();
    showNotification(
        isEditing.value
            ? "Class subject updated successfully"
            : "Class subject created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (classSubjectToDelete.value) {
            await classSubjectStore.deleteClassSubject(
                classSubjectToDelete.value.id,
            );
            showNotification("Class subject deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete class subject", "error");
    }
};

const goToPage = (page: number) => {
    classSubjectStore.setPage(page);
    fetchClassSubjects();
};

const resetFilters = async () => {
    searchQuery.value = "";
    filters.value.class_id = "";
    filters.value.subject_id = "";
    filters.value.teacher_id = "";
    filters.value.semester_id = "";
    filters.value.is_active = null;
    await classSubjectStore.resetFilters();
    fetchClassSubjects();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchClassSubjects();
    fetchOptions();
});
</script>
